import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Bronson DefinitionTable component.
 *
 * @param {string} [definitionTableTitle] - The main table title.
 * @param {DefinitionTableSection[]} [children] - A collection of {@link DefinitionTableSection}.
 * @param {boolean} [forceTitleWrap] - If the main title of the table gets to long we can force a wrap.
 * @param {boolean} [noWrap] - Controls whether the two columns wrap on smaller viewports.
 * @param {boolean} [withButton] - If you use buttons inside at least {@link DefinitionTableItem} it is recommended to set this.
 * @param {string} [className] - Additional classes on the list element.
 * @param {string} [testId] - Value for `[data-testid]` on the element.
 * @param {string} [otherProps] - Additional props to be passed to the children.
 * @return {JSX.Element} - The DefinitionTable component.
 * @constructor
 */
export function DefinitionTable({
  className,
  definitionTableTitle,
  testId,
  children,
  forceTitleWrap,
  noWrap,
  withButton,
  ...otherProps /* in <h3> tag */
}) {
  // generated
  const divClassNameList = classNames(
    'c-definition-table',
    {
      /**
       * Main class modifier convenience prop extension.
       */
      'c-definition-table--no-wrap ': noWrap, // Convenience prop from Bronson variants.
      'c-definition-table--with-button ': withButton, // Convenience prop from Bronson variants.
    },
    className
  ).trim()

  // generated
  function renderIfDefinitionTableTitle() {
    if (definitionTableTitle) {
      /**
       * Handle long titles with additional wrap class if specified.
       * @type {string}
       */
      const definitionTableTitleClassNameList = classNames(
        {
          'c-definition-table__title ': true,
          // Class modifier convenience prop extension
          'u-text-wrap ': forceTitleWrap, // Convenience prop from Bronson variants.
        },
        className
      ).trim()
      return (
        <h3 {...otherProps} data-testid={testId} className={definitionTableTitleClassNameList}>
          {definitionTableTitle}
        </h3>
      )
    }
    return null
  }

  // generated main result
  return (
    <div className={divClassNameList}>
      {renderIfDefinitionTableTitle()}
      {children /* Use 'DefinitionTable.Section' component. */}
    </div>
  )
}

DefinitionTable.propTypes = {
  className: PropTypes.string, // Bronson template: 'definition-table-modifier'.
  definitionTableTitle: PropTypes.string, // Bronson template: 'definition-table-title'.
  testId: PropTypes.string, // Added for data-testid attribute.
  children: PropTypes.node, // Bronson template: 'definition-table-section'. Use 'DefinitionTable.Section' component.
  /* Convenience props */
  noWrap: PropTypes.bool, // Convenience prop for c-definition-table--no-wrap (Bronson template: 'definition-table-modifier').
  withButton: PropTypes.bool, // Convenience prop for c-definition-table--with-button (Bronson template: 'definition-table-modifier').
  forceTitleWrap: PropTypes.bool, // Convenience prop for u-text-wrap (Bronson template: 'definition-table-title-force-wrap').
}

/**
 * Bronson DefinitionTableItem component (nested).
 *
 * @param {React.ReactNode} [title] - The title to display.
 * @param {React.ReactNode} [titleComment] - Smaller text below the title.
 * @param {React.ReactNode} [children] - The data cell to display.
 * @param {React.ReactNode} [dataComment] - Smaller text below the data cell.
 * @param {string} [testId] - Value for `[data-testid]` on the dt/dd element.
 * @param {string} [otherProps] - Additional props to be passed to the dt element.
 * @return {JSX.Element} - The DefinitionTableItem component.
 * @constructor
 */
function DefinitionTableItem({ title, titleComment, children, dataComment, testId, ...otherProps /* in <dt> tag */ }) {
  // generated
  function renderIfTitleComment() {
    if (titleComment) {
      return <span className="c-definition-table__item-title-comment">{titleComment}</span>
    }
    return null
  }

  // generated
  function renderIfDataComment() {
    if (dataComment) {
      return <span className="c-definition-table__item-data-comment">{dataComment}</span>
    }
    return null
  }

  // generated main result
  return (
    <>
      {title && (
        <dt className="c-definition-table__item-title" data-testid={testId} {...otherProps}>
          {title}
          {renderIfTitleComment()}
        </dt>
      )}
      <dd className="c-definition-table__item-data" data-testid={`${testId}-dd`}>
        {children}
        {renderIfDataComment()}
      </dd>
    </>
  )
}

DefinitionTableItem.propTypes = {
  title: PropTypes.node, // Bronson template: 'title'.
  titleComment: PropTypes.node, // Bronson template: 'title-comment'.
  children: PropTypes.node, // Bronson template: 'data'.
  dataComment: PropTypes.node, // Bronson template: 'data-comment'.
  testId: PropTypes.string, // Added for data-testid attribute.
}

DefinitionTableItem.displayName = 'DefinitionTable.Item'
DefinitionTable.Item = DefinitionTableItem

/**
 * Bronson DefinitionTableSection component (nested).
 *
 * @param {React.ReactNode} [innerTitle] - Displays a title at the top of the section.
 * @param {boolean} [summary] - Whether to render this section as the table’s summary.
 * @param {React.ReactNode} [summaryTitle] - The summary (row) title to display.
 * @param {React.ReactNode} [summaryData] - The data cell to display.
 * @param {React.ReactNode} [summaryInformation] - Additional content below the first summary row.
 * @param {DefinitionTableItem[]} [children] - A collection of {@link DefinitionTableItem} to render.
 * @return {JSX.Element} - The DefinitionTableSection component.
 * @constructor
 */
function DefinitionTableSection({
  innerTitle,
  summary,
  summaryTitle,
  summaryData,
  summaryInformation,
  children,
  // ...otherProps /* in no tag */
}) {
  // generated
  function renderIfDefinitionTableSectionInnerTitle() {
    if (innerTitle) {
      return <h4 className="c-definition-table__inner-title">{innerTitle}</h4>
    }
    return null
  }

  // generated
  function renderIfDefinitionTableSectionSummary() {
    if (summary) {
      return (
        <>
          <dl className="c-definition-table__section c-definition-table__section--summary">
            <dt className="c-definition-table__item-title">{summaryTitle}</dt>
            <dd className="c-definition-table__item-data">{summaryData}</dd>
          </dl>
          <p className="c-definition-table__information">{summaryInformation}</p>
        </>
      )
    }
    return null
  }

  // generated main result
  return (
    <>
      {renderIfDefinitionTableSectionInnerTitle()}
      <dl className="c-definition-table__section">{children /* Use 'DefinitionTable.Item' component. */}</dl>
      {renderIfDefinitionTableSectionSummary()}
    </>
  )
}

DefinitionTableSection.propTypes = {
  innerTitle: PropTypes.node, // Bronson template: 'definition-table-section-inner-title'.
  summary: PropTypes.bool, // Bronson template: 'definition-table-section-summary'.
  summaryTitle: PropTypes.node, // Bronson template: 'definition-table-section-summary-title'.
  summaryData: PropTypes.node, // Bronson template: 'definition-table-section-summary-data'.
  summaryInformation: PropTypes.node, // Bronson template: 'definition-table-section-summary-information'.
  children: PropTypes.node, // Bronson template: 'definition-table-item'. Use 'DefinitionTable.Item' component.
}

DefinitionTableSection.displayName = 'DefinitionTable.Section'
DefinitionTable.Section = DefinitionTableSection
